// Sharing a video that has ALREADY rendered, from the fullscreen player that is playing it.
//
// Every surface that shows a finished flyover or Trail Trailer — the recording page's thumb, the hike
// page's trailer thumb, a comment's flyover tile, My Flyovers' own player — plays it in a fullscreen
// player, so that player carries the Share button. It opens the existing share wizard straight on its
// ready step (`video` set on the config: no render, no reuse lookup), the same view My Flyovers' row
// share already opens.
//
// The player itself stays generic: it only knows "here is a share context, or not". Everything that
// differs per surface — kind, page link, title, analytics source — is decided here so it's
// unit-testable rather than spread across a DOM builder, two entrypoints and three components.

import { isTrailerVideo, videoCardTitle, videoShareUrl, type CardSubject } from '../flyover_videos/components/formatters';
import type { ShareKind } from './shareAnalytics';

/** The wire values the fullscreen players report. See shareAnalytics.ts for the full vocabulary. */
export type ReadyVideoShareSource =
  | 'recording_overview'
  | 'hike_overview'
  | 'comment'
  | 'my_flyovers'
  | 'my_flyovers_player';

const READY_VIDEO_SOURCES: readonly string[] = [
  'recording_overview',
  'hike_overview',
  'comment',
  'my_flyovers',
  'my_flyovers_player',
];

/** What a surface hands the player so it can offer Share. Absent → no Share button. */
export interface ReadyVideoShareContext {
  kind: ShareKind;
  source: ReadyVideoShareSource;
  /** The subject's page (recording or hike). The wizard only falls back to it with no video URL. */
  shareUrl: string;
  title: string;
  /** Lets "Save video" go through the download proxy instead of opening the raw file. */
  videoId?: string | null;
  /** Server aspect ('9x16' / 'PORTRAIT_9_16' / …) when the surface has one. */
  aspect?: string | null;
}

/** The share wizard config (FlyoverShare.vue's ShareConfig) for an already-rendered video. */
export interface ReadyVideoShareConfig {
  title: string;
  shareUrl: string;
  video: { url: string; aspect: string | null; id: string | null };
  kind: ShareKind;
  source: ReadyVideoShareSource;
}

export interface VideoDimensions {
  width?: number | null;
  height?: number | null;
}

/**
 * The aspect of a decoded video, in the stored '9x16' / '16x9' form the wizard's isVerticalAspect
 * reads. Null until the browser has the metadata (both dimensions 0), so the caller can tell "don't
 * know" from a real answer. A square video counts as vertical, matching the wizard's own default.
 */
export function aspectFromDimensions(dims: VideoDimensions | null | undefined): '9x16' | '16x9' | null {
  const w = Number(dims?.width);
  const h = Number(dims?.height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w > h ? '16x9' : '9x16';
}

/**
 * The aspect to open the wizard with: the server's when the surface has it, otherwise the shape of
 * the video the player has actually decoded. Without either, the wizard lays a landscape video out
 * in its portrait frame and reports `format: vertical` for it.
 */
export function resolveShareAspect(
  aspect: string | null | undefined,
  dims?: VideoDimensions | null,
): string | null {
  const stored = (aspect ?? '').trim();
  return stored || aspectFromDimensions(dims);
}

/** Wizard config for sharing `videoUrl` with `ctx`; `dims` is the playing video's decoded size. */
export function buildReadyVideoShareConfig(
  videoUrl: string,
  ctx: ReadyVideoShareContext,
  dims?: VideoDimensions | null,
): ReadyVideoShareConfig {
  return {
    title: ctx.title || '',
    shareUrl: ctx.shareUrl || '',
    video: {
      url: videoUrl,
      aspect: resolveShareAspect(ctx.aspect, dims),
      id: ctx.videoId != null && String(ctx.videoId).trim() !== '' ? String(ctx.videoId) : null,
    },
    kind: ctx.kind,
    source: ctx.source,
  };
}

/**
 * The share context a server-rendered thumb carries in its data-* attributes (recordings/show and
 * hikes/show). Null when the markup doesn't declare a known kind and source — an older or partial
 * thumb then simply plays without a Share button rather than sharing under the wrong bucket.
 */
export function shareContextFromDataset(
  ds: Record<string, string | undefined> | null | undefined,
): ReadyVideoShareContext | null {
  const kind = ds?.shareKind;
  const source = ds?.shareSource ?? '';
  if (kind !== 'flyover' && kind !== 'trailer') return null;
  if (!READY_VIDEO_SOURCES.includes(source)) return null;
  return {
    kind,
    source: source as ReadyVideoShareSource,
    shareUrl: ds?.shareUrl || '',
    title: ds?.shareTitle || '',
    videoId: ds?.videoId || null,
    aspect: ds?.videoAspect || null,
  };
}

export interface MyFlyoversRow extends CardSubject {
  id?: string | number | null;
  aspect?: string | null;
}

/**
 * A My Flyovers row. The row's share icon (`my_flyovers`) and the row's fullscreen player
 * (`my_flyovers_player`) share this one builder, so the two can only differ in their source.
 */
export function myFlyoversShareContext(
  video: MyFlyoversRow,
  source: 'my_flyovers' | 'my_flyovers_player',
): ReadyVideoShareContext {
  return {
    // This path opens straight onto an already-rendered video and deliberately carries no hikeId
    // (that would re-route the wizard's mutation), so the kind is stated explicitly.
    kind: isTrailerVideo(video) ? 'trailer' : 'flyover',
    source,
    shareUrl: videoShareUrl(video),
    title: videoCardTitle(video),
    videoId: video.id != null ? String(video.id) : null,
    aspect: video.aspect ?? null,
  };
}

/** A recording's public page — the link My Flyovers builds, made absolute against `origin`. */
export function recordingPageUrl(obfuscatedSlug: string | null | undefined, origin = ''): string {
  const slug = (obfuscatedSlug ?? '').trim();
  return slug ? `${origin.replace(/\/+$/, '')}/recordings/users/${slug}` : '';
}

export interface CommentRecordingLike {
  title?: string | null;
  obfuscatedSlug?: string | null;
  readyFlyoverVideoUrl?: string | null;
  flyoverVideos?: Array<{ id?: string | number | null; url?: string | null; aspect?: string | null } | null> | null;
}

/**
 * A comment's flyover tile. The tile plays `readyFlyoverVideoUrl`; the matching row of the same
 * recording's `flyoverVideos` (selected alongside it in the comments query) supplies the id and
 * aspect. Null when the comment has no playable flyover — the tile isn't shown then anyway.
 */
export function commentFlyoverShareContext(
  comment: { connectedRecording?: CommentRecordingLike | null } | null | undefined,
  origin = '',
): ReadyVideoShareContext | null {
  const recording = comment?.connectedRecording;
  const url = (recording?.readyFlyoverVideoUrl ?? '').trim();
  if (!recording || !url) return null;
  const video = (recording.flyoverVideos ?? []).find((v) => (v?.url ?? '').trim() === url) ?? null;
  return {
    kind: 'flyover',
    source: 'comment',
    shareUrl: recordingPageUrl(recording.obfuscatedSlug, origin),
    title: recording.title || '',
    videoId: video?.id != null ? String(video.id) : null,
    aspect: video?.aspect ?? null,
  };
}

type ShowFlyoverShare = (cfg: unknown) => void;

/** The share wizard's global entry point, or null on a page that doesn't mount `#flyover_share`. */
export function flyoverShareOpener(): ShowFlyoverShare | null {
  const show = (window as unknown as { showFlyoverShare?: ShowFlyoverShare }).showFlyoverShare;
  return typeof show === 'function' ? show : null;
}
